import React, { useState } from 'react';
import { Check, ChevronRight, ChevronDown } from 'lucide-react';
import { NoRecordIcon } from '../UI/SvgIcons';
import TableLoader from '../UI/loaders/TableLoader';

export interface TableColumn<T = unknown> {
  key: string;
  header: string;
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
  headerClassName?: string;
}

export interface Action<T> {
  text: string;
  onClick: (row: T) => void;
}

export interface TableProps<T = unknown, C = unknown> {
  columns: TableColumn<T>[];
  data: T[];
  childColumns?: TableColumn<C>[];

  showCheckbox?: boolean;
  onSelectAll?: (checked: boolean) => void;
  onSelectItem?: (item: T, checked: boolean, index: number) => void;
  selectedItems?: Set<string | number>;
  getItemId?: (item: T) => string | number;
  getChildId?: (child: C) => string | number;

  className?: string;
  tableClassName?: string;
  minHeight?: string;
  emptyMessage?: string;
  loading?: boolean;

  actions?: Action<T>[];
  childActions?: Action<C>[];
  getChildren?: (item: T) => C[] | undefined;
  level?: number;
}

const CommonNestedTable = <
  T extends Record<string, any>,
  C extends Record<string, any> = T,
>({
  columns,
  data,
  childColumns,
  showCheckbox = false,
  onSelectAll,
  onSelectItem,
  selectedItems = new Set(),
  getItemId = (item) => item.id,
  getChildId = (child) => (child as any).id,
  className = '',
  tableClassName = '',
  emptyMessage = 'No data available',
  loading = false,
  actions,
  childActions,
  getChildren,
  level = 0,
}: TableProps<T, C>) => {
  const allSelected =
    data.length > 0 && data.every((item) => selectedItems.has(getItemId(item)));

  const [expandedRow, setExpandedRow] = useState<string | number | null>(null);

  const toggleExpand = (id: string | number) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  return (
    <div className={`overflow-auto flex flex-col justify-between ${className}`}>
      <table className={`w-full text-left ${tableClassName}`}>
        <thead>
          <tr className="*:text-blue-400 *:text-sm *:py-4 *:px-5 *:border-b *:border-gray-200 *:font-medium">
            {showCheckbox && (
              <th>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    className="w-4 h-4 peer hidden"
                  />
                  <span className="w-5 h-5 rounded border-2 border-light-40 flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary">
                    <Check color="#fff" />
                  </span>
                </label>
              </th>
            )}
            {getChildren && <th></th>}
            {columns.map((column) => (
              <th key={column.key} className={column.headerClassName || ''}>
                {column.header}
              </th>
            ))}
            {actions && <th className="cell">Actions</th>}
          </tr>
        </thead>
        <tbody
          className="divide-y"
          uk-scrollspy="target: > tr; cls: uk-animation-slide-bottom-small; delay: 100 "
        >
          {loading ? (
            <TableLoader
              columns={
                columns.length +
                (showCheckbox ? 1 : 0) +
                (getChildren ? 1 : 0) +
                (actions ? 1 : 0)
              }
              rows={5}
            />
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={
                  columns.length +
                  (showCheckbox ? 1 : 0) +
                  (getChildren ? 1 : 0) +
                  (actions ? 1 : 0)
                }
                className="text-center py-8 text-gray-500"
              >
                <div className="flex flex-col justify-center items-center my-10">
                  <NoRecordIcon />
                  <span className="mt-2">{emptyMessage}</span>
                </div>
              </td>
            </tr>
          ) : (
            data.map((item, index) => {
              const itemId = getItemId(item);
              const isSelected = selectedItems.has(itemId);
              const children = getChildren?.(item) || [];
              const expanded = expandedRow === itemId;

              return (
                <React.Fragment key={itemId}>
                  <tr className="text-sm *:border-b *:border-gray-200 *:py-4 *:px-5 *:text-sm *:font-normal">
                    {showCheckbox && (
                      <td>
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) =>
                              onSelectItem?.(item, e.target.checked, index)
                            }
                            className="w-4 h-4 peer hidden"
                          />
                          <span className="w-5 h-5 rounded border-2 border-light-40 flex items-center justify-center peer-checked:bg-primary peer-checked:border-primary">
                            <Check color="#fff" />
                          </span>
                        </label>
                      </td>
                    )}

                    {getChildren && (
                      <td>
                        {children.length > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleExpand(itemId)}
                          >
                            {expanded ? (
                              <ChevronDown size={18} />
                            ) : (
                              <ChevronRight size={18} />
                            )}
                          </button>
                        )}
                      </td>
                    )}

                    {columns.map((column) => (
                      <td
                        key={`${itemId}-${column.key}`}
                        className={column.className || ''}
                      >
                        {column.render
                          ? column.render(item, index)
                          : item[column.key]}
                      </td>
                    ))}

                    {actions && (
                      <td>
                        <button className="uk-inline">
                          <ChevronRight />
                          {actions.length > 0 && (
                            <div
                              className="rounded-lg"
                              data-uk-dropdown="pos: bottom-right"
                            >
                              <ul className="uk-nav uk-dropdown-nav p-2 text-base">
                                {actions.map((action, i) => (
                                  <li key={i}>
                                    <button
                                      onClick={() => action.onClick(item)}
                                      className="flex items-center text-black text-sm capitalize gap-1.5 px-3 py-3 w-full rounded-lg hover:bg-primary-light"
                                    >
                                      {action.text}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </button>
                      </td>
                    )}
                  </tr>

                  {expanded && children.length > 0 && (
                    <tr>
                      <td
                        colSpan={
                          columns.length +
                          (showCheckbox ? 1 : 0) +
                          (getChildren ? 1 : 0) +
                          (actions ? 1 : 0)
                        }
                      >
                        <div className="pl-5 block">
                          <CommonNestedTable<C, any>
                            columns={
                              (childColumns as TableColumn<C>[]) ||
                              (columns as any)
                            }
                            data={children}
                            showCheckbox={showCheckbox}
                            onSelectAll={onSelectAll as any}
                            onSelectItem={onSelectItem as any}
                            selectedItems={selectedItems}
                            getItemId={
                              (getChildId as any) || (getItemId as any)
                            }
                            actions={(childActions as any) || (actions as any)}
                            getChildren={getChildren as any}
                            childColumns={childColumns as any}
                            childActions={childActions as any}
                            level={level + 1}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CommonNestedTable;
